import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import {
  CheckCircle2, Circle, Phone, Bot, PhoneIncoming, Users, ArrowUpRight,
  Clock, PhoneForwarded, Bell, Bookmark, Lightbulb, TrendingUp,
  RefreshCw, Flame, BarChart3, FileText, Mail, PhoneCall, Building2, TrendingDown,
  Trophy, Kanban, Share2, Copy, Check, Gift,
} from 'lucide-react';
import axios from 'axios';
import { useScheduleRecommendations, ScheduleSuggestionsCard } from '../components/ScheduleSuggestions';
import { UsageDashboardWidget } from '../components/UsageDashboardWidget';
import { UpgradeModal, useUpgradeModal } from '../components/UpgradeModal';
import { SetupNudges } from '../components/SetupNudges';
import { FirstWinBanner } from '../components/ActivationWidgets';
import RoiImpactPanel from '../components/RoiImpactPanel';
import AiCoachPanel from '../components/AiCoachPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTENT_LABELS = {
  buyer_inquiry: { label: 'Buyer', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  seller_inquiry: { label: 'Seller', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  property_search: { label: 'Search', color: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20' },
  showing_request: { label: 'Showing', color: 'bg-teal-500/10 text-teal-400 border-teal-500/20' },
  appointment_request: { label: 'Appointment', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  callback_request: { label: 'Callback', color: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' },
  general_question: { label: 'General', color: 'bg-secondary text-muted-foreground border-border' },
};

function formatDuration(sec) {
  if (!sec) return '0s';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffHrs = (now - d) / (1000 * 60 * 60);
  if (diffHrs < 1) return `${Math.round((now - d) / 60000)}m ago`;
  if (diffHrs < 24) return `${Math.round(diffHrs)}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const IMPORTANCE_STYLES = {
  high: 'border-l-amber-400 bg-amber-500/5',
  medium: 'border-l-blue-400 bg-blue-500/5',
  low: 'border-l-muted-foreground bg-muted',
};

const IMPORTANCE_ICONS = {
  high: <Flame size={14} className="text-amber-400" />,
  medium: <TrendingUp size={14} className="text-blue-400" />,
  low: <Lightbulb size={14} className="text-muted-foreground" />,
};

export default function DashboardPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [insights, setInsights] = useState([]);
  const [recentReports, setRecentReports] = useState([]);
  const [outboundStats, setOutboundStats] = useState(null);
  const [matchStats, setMatchStats] = useState(null);
  const [opportunities, setOpportunities] = useState(null);
  const [dealOpportunities, setDealOpportunities] = useState([]);
  const [scheduledCalls, setScheduledCalls] = useState([]);
  const [schedStats, setSchedStats] = useState(null);
  const { recs: schedRecs } = useScheduleRecommendations(token);
  const { isOpen: upgradeOpen, trigger: upgradeTrigger, openUpgrade, closeUpgrade } = useUpgradeModal();
  const [loading, setLoading] = useState(true);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [pipelineCounts, setPipelineCounts] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [dashRes, analyticsRes, insightsRes, reportsRes, obRes, matchRes, opRes, dealOpRes, schedRes, schedStatsRes, pipelineRes] = await Promise.all([
          axios.get(`${API}/agents/me/dashboard`, { headers }),
          axios.get(`${API}/analytics/overview`, { headers }).catch(() => ({ data: null })),
          axios.get(`${API}/analytics/insights`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/reports?limit=5`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/calls/outbound/stats`, { headers }).catch(() => ({ data: null })),
          axios.get(`${API}/property-matches/stats`, { headers }).catch(() => ({ data: null })),
          axios.get(`${API}/property-analysis/opportunities`, { headers }).catch(() => ({ data: null })),
          axios.get(`${API}/deal-scores/opportunities?limit=6`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/scheduled-calls?status=pending`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/scheduled-calls/stats`, { headers }).catch(() => ({ data: null })),
          axios.get(`${API}/leads/pipeline/board`, { headers }).catch(() => ({ data: null })),
        ]);
        setData(dashRes.data);
        setAnalytics(analyticsRes.data);
        setInsights(Array.isArray(insightsRes.data) ? insightsRes.data : []);
        setRecentReports(Array.isArray(reportsRes.data) ? reportsRes.data : []);
        setOutboundStats(obRes.data);
        setMatchStats(matchRes.data);
        setOpportunities(opRes.data);
        setDealOpportunities(Array.isArray(dealOpRes.data) ? dealOpRes.data : []);
        setScheduledCalls(Array.isArray(schedRes.data) ? schedRes.data : []);
        setSchedStats(schedStatsRes.data);
        setPipelineCounts(pipelineRes.data?.counts || null);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [token]);

  const refreshInsights = async () => {
    setInsightsLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const { data: genData } = await axios.post(`${API}/analytics/insights/generate`, {}, { headers });
      setInsights(genData.insights || []);
    } catch (err) {
      console.error('Insight generation failed:', err);
    } finally {
      setInsightsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent" />
      </div>
    );
  }

  const agent = data?.agent || user;
  const checklist = data?.onboarding_checklist || {};
  const phone = data?.phone;
  const assistant = data?.assistant;
  const recentCalls = data?.recent_calls || [];

  const calls = analytics?.calls || {};
  const leads = analytics?.leads || {};
  const searches = analytics?.searches || {};
  const intents = analytics?.intents || {};

  const checklistItems = [
    { key: 'account_created', label: 'Account created', done: checklist.account_created },
    { key: 'assistant_configured', label: 'AI assistant configured', done: checklist.assistant_configured },
    { key: 'phone_assigned', label: 'Phone number assigned', done: checklist.phone_assigned },
    { key: 'first_call_received', label: 'First call received', done: checklist.first_call_received },
    { key: 'first_lead_captured', label: 'First lead captured', done: checklist.first_lead_captured },
  ];
  const completedCount = checklistItems.filter((i) => i.done).length;
  const progressPct = Math.round((completedCount / checklistItems.length) * 100);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-5 sm:space-y-6" data-testid="dashboard-page">
      {/* Activation Banner */}
      <FirstWinBanner />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">
            Welcome back, {agent?.full_name?.split(' ')[0] || 'Agent'}
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            {agent?.brokerage_name} &middot; {agent?.display_market}
          </p>
        </div>
        <Button size="sm" onClick={() => navigate('/referrals')}
          className="h-8 text-xs px-3 gap-1.5 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/20"
          data-testid="dashboard-share-ai-btn">
          <Gift size={13} /> Share My AI
        </Button>
      </div>

      {/* Key Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={<PhoneIncoming size={18} />} iconBg="bg-blue-500/10" iconColor="text-blue-400"
          value={calls.calls_last_7_days ?? calls.total_calls ?? 0} label="Calls This Week" />
        <StatCard icon={<Flame size={18} />} iconBg="bg-red-500/10" iconColor="text-red-400"
          value={leads.hot_leads ?? 0} label="Hot Leads" accent="red" />
        <StatCard icon={<Users size={18} />} iconBg="bg-emerald-500/10" iconColor="text-emerald-400"
          value={leads.total_leads ?? 0} label="Total Leads" accent="emerald" />
        <StatCard icon={<Bookmark size={18} />} iconBg="bg-indigo-500/10" iconColor="text-indigo-400"
          value={searches.active_saved_searches ?? 0} label="Saved Searches" accent="indigo" />
      </div>

      {/* AI Impact Panel */}
      <RoiImpactPanel />

      {/* AI Coach Panel */}
      <AiCoachPanel />

      {/* Usage Widget & Warning Banners */}
      <UsageDashboardWidget onUpgrade={openUpgrade} />

      {/* Setup Nudges for incomplete onboarding */}
      <SetupNudges />

      {/* Pipeline Summary */}
      {pipelineCounts && (
        <Card className="border-border bg-card cursor-pointer hover:border-border transition-colors" data-testid="pipeline-summary-card" onClick={() => navigate('/pipeline')}>
          <CardHeader className="pb-2">
            <CardTitle className="text-foreground text-base flex items-center gap-2">
              <Kanban size={16} className="text-violet-400" />
              Deal Pipeline
              <ArrowUpRight size={14} className="ml-auto text-muted-foreground" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-1 overflow-x-auto pb-1">
              {[
                { key: 'new_lead', label: 'New', color: 'bg-sky-500' },
                { key: 'contacted', label: 'Contacted', color: 'bg-blue-500' },
                { key: 'qualified', label: 'Qualified', color: 'bg-emerald-500' },
                { key: 'showing_scheduled', label: 'Showing', color: 'bg-violet-500' },
                { key: 'offer_made', label: 'Offer', color: 'bg-amber-500' },
                { key: 'under_contract', label: 'Contract', color: 'bg-orange-500' },
                { key: 'closed', label: 'Closed', color: 'bg-green-500' },
                { key: 'lost', label: 'Lost', color: 'bg-muted-foreground' },
              ].map(s => {
                const count = pipelineCounts[s.key] || 0;
                return (
                  <div key={s.key} className="flex flex-col items-center flex-1 min-w-[60px]">
                    <span className={`text-lg font-bold ${count > 0 ? 'text-foreground' : 'text-muted-foreground'}`}>{count}</span>
                    <div className={`h-1 w-full rounded-full mt-1 ${count > 0 ? s.color : 'bg-muted'}`} />
                    <span className="text-[10px] text-muted-foreground mt-1 whitespace-nowrap">{s.label}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Outbound Calling Activity */}
      {outboundStats && outboundStats.total > 0 && (
        <Card className="border-border bg-card" data-testid="outbound-activity">
          <CardHeader className="pb-2">
            <CardTitle className="text-foreground text-base flex items-center gap-2">
              <PhoneCall size={16} className="text-teal-400" /> Outbound Calls Today
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-muted rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-foreground">{outboundStats.total}</p>
                <p className="text-[10px] text-muted-foreground">Total</p>
              </div>
              <div className="bg-muted rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-emerald-400">{outboundStats.answered}</p>
                <p className="text-[10px] text-muted-foreground">Answered ({outboundStats.total ? Math.round(outboundStats.answered / outboundStats.total * 100) : 0}%)</p>
              </div>
              <div className="bg-muted rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-blue-400">{outboundStats.transferred}</p>
                <p className="text-[10px] text-muted-foreground">Transferred</p>
              </div>
              <div className="bg-muted rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-violet-400">{outboundStats.appointments}</p>
                <p className="text-[10px] text-muted-foreground">Appointments</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upcoming Scheduled Calls */}
      {scheduledCalls.length > 0 && (
        <Card className="border-border bg-card" data-testid="scheduled-calls-widget">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-foreground text-base flex items-center gap-2">
                <Clock size={16} className="text-teal-400" /> Upcoming Scheduled Calls
              </CardTitle>
              <Button size="sm" variant="ghost" className="text-xs text-muted-foreground hover:text-foreground gap-1"
                onClick={() => navigate('/scheduled-calls')} data-testid="goto-scheduled-calls">
                View All <ArrowUpRight size={12} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {scheduledCalls.slice(0, 5).map(sc => (
                <div key={sc.id} className="flex items-center justify-between bg-muted rounded-lg px-3 py-2.5" data-testid={`dash-scheduled-${sc.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-1.5 rounded-lg bg-teal-500/10 shrink-0">
                      <Clock size={14} className="text-teal-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{sc.lead_name || 'Lead'}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {new Date(sc.scheduled_at).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        {sc.purpose && ` — ${sc.purpose.replace(/_/g, ' ')}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={`text-[9px] ${
                      sc.lead_temperature === 'hot' ? 'bg-red-500/10 text-red-400 border-red-500/20' :
                      sc.lead_temperature === 'warm' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                      'bg-secondary text-muted-foreground border-border'
                    }`}>{sc.lead_type}</Badge>
                    <Button size="sm" variant="ghost" className="text-[10px] text-muted-foreground hover:text-foreground h-6 px-2"
                      onClick={() => navigate('/scheduled-calls')}>
                      View <ArrowUpRight size={10} className="ml-0.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scheduled Call Analytics */}
      {schedStats && schedStats.total > 0 && (
        <Card className="border-border bg-card" data-testid="sched-analytics-widget">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-foreground text-base flex items-center gap-2">
                <BarChart3 size={16} className="text-violet-400" /> Scheduled Call Analytics
              </CardTitle>
              <Button size="sm" variant="ghost" className="text-xs text-muted-foreground hover:text-foreground gap-1"
                onClick={() => navigate('/scheduled-calls')} data-testid="goto-sched-analytics">
                Manage <ArrowUpRight size={12} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
              <div className="bg-muted rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-foreground">{schedStats.total}</p>
                <p className="text-[10px] text-muted-foreground">Total</p>
              </div>
              <div className="bg-emerald-500/5 rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-emerald-400">{schedStats.success_rate}%</p>
                <p className="text-[10px] text-muted-foreground">Success Rate</p>
              </div>
              <div className="bg-blue-500/5 rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-blue-400">{schedStats.upcoming}</p>
                <p className="text-[10px] text-muted-foreground">Upcoming</p>
              </div>
              <div className="bg-orange-500/5 rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-orange-400">{schedStats.deferred}</p>
                <p className="text-[10px] text-muted-foreground">Deferred</p>
              </div>
              <div className="bg-red-500/5 rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-red-400">{schedStats.retried}</p>
                <p className="text-[10px] text-muted-foreground">Retried</p>
              </div>
            </div>
            {/* Purpose breakdown */}
            {Object.keys(schedStats.by_purpose || {}).length > 0 && (
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">By Purpose</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(schedStats.by_purpose).map(([purpose, count]) => {
                    const colors = {
                      follow_up: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
                      showing_reminder: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
                      new_lead_outreach: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
                      seller_follow_up: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
                      custom: 'bg-secondary text-muted-foreground border-border',
                    };
                    return (
                      <Badge key={purpose} className={`text-xs ${colors[purpose] || colors.custom}`} data-testid={`purpose-stat-${purpose}`}>
                        {purpose.replace(/_/g, ' ')}: {count}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}
            {/* This week breakdown */}
            {Object.keys(schedStats.this_week || {}).length > 0 && (
              <div className="mt-3 pt-3 border-t border-border">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">This Week</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(schedStats.this_week).map(([status, count]) => {
                    const statusColors = {
                      pending: 'text-blue-400',
                      completed: 'text-emerald-400',
                      failed: 'text-red-400',
                      cancelled: 'text-muted-foreground',
                      deferred: 'text-orange-400',
                    };
                    return (
                      <span key={status} className="text-xs text-muted-foreground">
                        <span className={statusColors[status] || 'text-foreground'}>{count}</span> {status}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Smart Scheduling Insight */}
      {schedRecs && (
        <ScheduleSuggestionsCard recs={schedRecs} compact />
      )}

      {/* Property Match Activity */}
      {matchStats && matchStats.total > 0 && (
        <Card className="border-border bg-card" data-testid="match-activity">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-foreground text-base flex items-center gap-2">
                <Building2 size={16} className="text-teal-400" /> Property Matches
              </CardTitle>
              <Button size="sm" variant="ghost" className="text-xs text-muted-foreground hover:text-foreground"
                onClick={() => navigate('/properties')} data-testid="view-properties-btn">
                View All <ArrowUpRight size={12} className="ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-muted rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-foreground">{matchStats.total}</p>
                <p className="text-[10px] text-muted-foreground">Total Matches</p>
              </div>
              <div className="bg-emerald-500/5 rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-emerald-400">{matchStats.new_match}</p>
                <p className="text-[10px] text-muted-foreground">New Matches</p>
              </div>
              <div className="bg-blue-500/5 rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-blue-400">{matchStats.price_drop}</p>
                <p className="text-[10px] text-muted-foreground">Price Drops</p>
              </div>
              <div className="bg-amber-500/5 rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-amber-400">{matchStats.unread}</p>
                <p className="text-[10px] text-muted-foreground">Unread</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Smart Insights + Recent Calls */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Deal Opportunities Widget */}
        {dealOpportunities.length > 0 && (
          <Card className="border-border bg-card lg:col-span-2" data-testid="deal-opportunities-widget">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-foreground text-base flex items-center gap-2">
                  <Trophy size={16} className="text-amber-400" /> Top Deal Opportunities
                </CardTitle>
                <Button size="sm" variant="ghost" className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => navigate('/properties')} data-testid="view-all-deals-btn">
                  View All <ArrowUpRight size={12} className="ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {dealOpportunities.slice(0, 6).map(ds => {
                  const levelColor = ds.opportunity_level === 'exceptional' ? 'text-amber-400 bg-amber-500/10'
                    : ds.opportunity_level === 'strong' ? 'text-emerald-400 bg-emerald-500/10'
                    : 'text-blue-400 bg-blue-500/10';
                  return (
                    <div key={ds.id || ds.property_id} className="bg-muted rounded-lg p-3 hover:bg-accent cursor-pointer transition-colors"
                      onClick={() => navigate('/properties')} data-testid={`deal-opp-${ds.property_id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className={`flex items-center gap-1.5 text-xs font-bold ${levelColor.split(' ')[0]}`}>
                          <Trophy size={14} /> {ds.score}/100
                        </div>
                        <Badge className={`text-[9px] ${levelColor}`}>{ds.opportunity_level}</Badge>
                      </div>
                      <p className="text-[10px] text-muted-foreground truncate">ID: {ds.listing_id || ds.property_id?.slice(0, 8)}</p>
                      {ds.reasons?.length > 0 && (
                        <p className="text-[10px] text-muted-foreground mt-1 truncate">{ds.reasons[0]}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Top Opportunities Widget */}
        {opportunities && (opportunities.top_opportunities?.length > 0 || opportunities.price_drop_opportunities?.length > 0) && (
          <Card className="border-border bg-card lg:col-span-2" data-testid="opportunities-widget">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-foreground text-base flex items-center gap-2">
                  <TrendingUp size={16} className="text-emerald-400" /> Property Opportunities
                </CardTitle>
                <Button size="sm" variant="ghost" className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => navigate('/properties')} data-testid="view-all-opportunities-btn">
                  View All <ArrowUpRight size={12} className="ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(opportunities.top_opportunities || []).slice(0, 4).map(a => (
                  <div key={a.id} className="bg-muted rounded-lg p-3 hover:bg-accent cursor-pointer transition-colors"
                    onClick={() => navigate('/properties')} data-testid={`opportunity-${a.id}`}>
                    <div className="flex items-start gap-2">
                      <div className={`h-7 w-7 rounded flex items-center justify-center shrink-0 ${
                        a.value_signal === 'strong' ? 'bg-emerald-500/10' : 'bg-blue-500/10'
                      }`}>
                        <Building2 size={14} className={a.value_signal === 'strong' ? 'text-emerald-400' : 'text-blue-400'} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-foreground font-medium truncate">{a.inputs_snapshot?.list_price ? `$${a.inputs_snapshot.list_price.toLocaleString()}` : ''} — {a.summary?.split(' — ')[0] || 'Property'}</p>
                        <p className="text-[10px] text-muted-foreground truncate mt-0.5">{a.summary?.substring(0, 80)}...</p>
                        <div className="flex gap-1.5 mt-1.5">
                          {a.opportunity_tags?.slice(0, 3).map((t, i) => (
                            <span key={i} className="text-[9px] bg-teal-500/10 text-teal-400 px-1.5 py-0.5 rounded">{t.replace(/_/g, ' ')}</span>
                          ))}
                          <span className={`text-[9px] px-1.5 py-0.5 rounded ${
                            a.value_signal === 'strong' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-blue-500/10 text-blue-400'
                          }`}>{a.value_signal} value</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Smart Insights Panel */}
        <Card className="border-border bg-card" data-testid="insights-panel">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-foreground text-base flex items-center gap-2">
                <Lightbulb size={16} className="text-amber-400" />
                Smart Insights
              </CardTitle>
              <Button
                size="sm" variant="ghost"
                onClick={refreshInsights}
                disabled={insightsLoading}
                className="h-7 text-xs text-muted-foreground hover:text-foreground gap-1"
                data-testid="refresh-insights-btn"
              >
                <RefreshCw size={12} className={insightsLoading ? 'animate-spin' : ''} />
                {insightsLoading ? 'Generating...' : 'Refresh'}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {insights.length === 0 ? (
              <div className="py-6 text-center">
                <Lightbulb className="h-6 w-6 text-muted-foreground mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">Click Refresh to generate insights</p>
              </div>
            ) : (
              insights.slice(0, 6).map((insight, i) => (
                <div
                  key={insight.id || i}
                  className={`border-l-2 rounded-r-lg px-3 py-2 ${IMPORTANCE_STYLES[insight.importance] || IMPORTANCE_STYLES.low}`}
                  data-testid={`insight-${i}`}
                >
                  <div className="flex items-start gap-2">
                    <span className="mt-0.5 shrink-0">{IMPORTANCE_ICONS[insight.importance]}</span>
                    <p className="text-sm text-foreground">{insight.insight_text}</p>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Recent Calls */}
        <Card className="border-border bg-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-foreground text-base">Recent Calls</CardTitle>
              <button onClick={() => navigate('/call-history')}
                className="text-xs text-emerald-400 hover:text-primary inline-flex items-center gap-1"
                data-testid="goto-call-history">
                View all <ArrowUpRight size={12} />
              </button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {recentCalls.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No calls yet</p>
            ) : (
              recentCalls.slice(0, 5).map(call => {
                const intentInfo = INTENT_LABELS[call.intent] || INTENT_LABELS.general_question;
                return (
                  <div key={call.id}
                    className="flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-accent cursor-pointer transition-colors"
                    onClick={() => navigate('/call-history')}
                    data-testid={`recent-call-${call.id}`}
                  >
                    <PhoneIncoming size={14} className="text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-foreground truncate block">{call.caller_number || 'Unknown'}</span>
                      <span className="text-xs text-muted-foreground">{formatTime(call.started_at || call.created_at)}</span>
                    </div>
                    <Badge className={`text-[10px] ${intentInfo.color}`}>{intentInfo.label}</Badge>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      </div>

      {/* Analytics Detail Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Call Analytics */}
        <Card className="border-border bg-card" data-testid="call-analytics-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 size={14} className="text-blue-400" />
              <h3 className="text-sm font-medium text-foreground">Call Analytics</h3>
            </div>
            <div className="space-y-2">
              <AnalyticsRow label="This week" value={calls.calls_last_7_days ?? 0} />
              <AnalyticsRow label="Last 30 days" value={calls.calls_last_30_days ?? 0} />
              <AnalyticsRow label="Completed" value={calls.completed_calls ?? 0} />
              <AnalyticsRow label="Transferred" value={calls.transferred_calls ?? 0} />
              <AnalyticsRow label="Avg duration" value={formatDuration(Math.round(calls.avg_duration ?? 0))} />
            </div>
          </CardContent>
        </Card>

        {/* Lead Intelligence */}
        <Card className="border-border bg-card" data-testid="lead-intelligence-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Users size={14} className="text-emerald-400" />
                <h3 className="text-sm font-medium text-foreground">Lead Intelligence</h3>
              </div>
              <button onClick={() => navigate('/leads')} className="text-xs text-emerald-400 hover:text-primary" data-testid="goto-leads">
                <ArrowUpRight size={12} />
              </button>
            </div>
            <div className="space-y-2">
              <AnalyticsRow label="Hot leads" value={leads.hot_leads ?? 0} valueColor="text-red-400" />
              <AnalyticsRow label="Warm leads" value={leads.warm_leads ?? 0} valueColor="text-amber-400" />
              <AnalyticsRow label="Buyer leads" value={leads.buyer_leads ?? 0} valueColor="text-blue-400" />
              <AnalyticsRow label="Seller leads" value={leads.seller_leads ?? 0} valueColor="text-amber-400" />
              <AnalyticsRow label="Callbacks needed" value={leads.callbacks_needed ?? 0} valueColor="text-orange-400" />
            </div>
          </CardContent>
        </Card>

        {/* Search + Alerts */}
        <Card className="border-border bg-card" data-testid="search-analytics-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Bookmark size={14} className="text-indigo-400" />
                <h3 className="text-sm font-medium text-foreground">Searches & Alerts</h3>
              </div>
              <button onClick={() => navigate('/saved-searches')} className="text-xs text-emerald-400 hover:text-primary" data-testid="goto-saved-searches">
                <ArrowUpRight size={12} />
              </button>
            </div>
            <div className="space-y-2">
              <AnalyticsRow label="Active searches" value={searches.active_saved_searches ?? 0} />
              <AnalyticsRow label="Avg results" value={Math.round(searches.avg_results_per_search ?? 0).toLocaleString()} />
              <AnalyticsRow label="Unread alerts" value={searches.alert_candidates ?? 0} valueColor={searches.alert_candidates > 0 ? 'text-amber-400' : undefined} />
              {(searches.top_searches || []).slice(0, 2).map((s, i) => (
                <div key={i} className="text-xs text-muted-foreground truncate">
                  {s.name}: <span className="text-foreground">{s.last_result_count?.toLocaleString()}</span> results
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Reports Widget */}
      {recentReports.length > 0 && (
        <Card className="border-border bg-card" data-testid="recent-reports-widget">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-foreground text-base flex items-center gap-2">
                <FileText size={16} className="text-blue-400" />
                Recent Reports
              </CardTitle>
              <button onClick={() => navigate('/reports')}
                className="text-xs text-emerald-400 hover:text-primary inline-flex items-center gap-1"
                data-testid="goto-reports">
                View all <ArrowUpRight size={12} />
              </button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {recentReports.slice(0, 4).map(r => {
              const typeLabel = r.report_type === 'daily_digest' ? 'Daily' : r.report_type === 'weekly_digest' ? 'Weekly' : 'Alert';
              const typeColor = r.report_type === 'daily_digest' ? 'text-emerald-400' : r.report_type === 'weekly_digest' ? 'text-blue-400' : 'text-amber-400';
              return (
                <div key={r.id} className="flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-accent cursor-pointer transition-colors"
                  onClick={() => navigate('/reports')} data-testid={`recent-report-${r.id}`}>
                  <Mail size={14} className={typeColor} />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-foreground truncate block">{r.subject}</span>
                    <span className="text-xs text-muted-foreground">{r.summary?.slice(0, 60)}</span>
                  </div>
                  <Badge className={`text-[10px] bg-muted ${typeColor} border-border shrink-0`}>{typeLabel}</Badge>
                  <span className="text-xs text-muted-foreground shrink-0 hidden sm:block">{formatTime(r.created_at)}</span>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Bottom row: Onboarding + Phone/AI */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-border bg-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-foreground text-base">Onboarding Progress</CardTitle>
              <span className="text-xs text-muted-foreground">{completedCount}/{checklistItems.length}</span>
            </div>
            <Progress value={progressPct} className="h-1.5 mt-2 bg-muted [&>div]:bg-emerald-500" />
          </CardHeader>
          <CardContent className="space-y-3">
            {checklistItems.map(item => (
              <div key={item.key} className="flex items-center gap-3" data-testid={`checklist-${item.key}`}>
                {item.done ? <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" /> : <Circle className="h-4 w-4 text-muted-foreground shrink-0" />}
                <span className={`text-sm ${item.done ? 'text-foreground' : 'text-muted-foreground'}`}>{item.label}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-border bg-card">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Phone className="h-4 w-4 text-purple-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground truncate">{phone?.phone_number || 'Pending'}</p>
                <p className="text-[10px] text-muted-foreground">Assigned Number</p>
              </div>
              <Badge className={`text-[10px] ${phone?.is_active ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-amber-500/10 text-amber-400 border-amber-500/20'}`} data-testid="phone-status-badge">
                {phone?.is_active ? 'Active' : 'Pending'}
              </Badge>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <Bot className="h-4 w-4 text-emerald-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-foreground">{assistant?.assistant_name || 'Ava'}</p>
                <p className="text-[10px] text-muted-foreground">Voice: {assistant?.voice || 'female'}</p>
              </div>
              <a href="/settings/assistant" className="text-xs text-emerald-400 hover:text-primary" data-testid="goto-assistant-settings"><ArrowUpRight size={12} /></a>
            </div>
            {/* Intent Breakdown */}
            {(intents.buyer_inquiries > 0 || intents.seller_inquiries > 0) && (
              <div className="border-t border-border pt-3 mt-1">
                <p className="text-xs text-muted-foreground mb-2">Intent Breakdown</p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(intents.breakdown || {}).slice(0, 5).map(([intent, count]) => {
                    const info = INTENT_LABELS[intent] || INTENT_LABELS.general_question;
                    return (
                      <Badge key={intent} className={`text-[10px] ${info.color}`}>{info.label}: {count}</Badge>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal isOpen={upgradeOpen} onClose={closeUpgrade} trigger={upgradeTrigger} currentTier={null} />
    </div>
  );
}

function StatCard({ icon, iconBg, iconColor, value, label, accent }) {
  const accentMap = { red: 'text-red-400', emerald: 'text-emerald-400', indigo: 'text-indigo-400' };
  return (
    <Card className="border-border bg-card">
      <CardContent className="flex items-center gap-3 p-4">
        <div className={`h-9 w-9 rounded-lg ${iconBg} flex items-center justify-center shrink-0`}>
          <span className={iconColor}>{icon}</span>
        </div>
        <div>
          <p className={`text-xl font-bold ${accent ? accentMap[accent] || 'text-foreground' : 'text-foreground'}`}>{value}</p>
          <p className="text-[10px] text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function AnalyticsRow({ label, value, valueColor }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-sm font-medium ${valueColor || 'text-foreground'}`}>{value}</span>
    </div>
  );
}
